/* eslint-disable immutable/no-mutation */
/* eslint-disable no-restricted-imports */
import { useEffect, useRef } from 'react';
import getFreebetReward from 'app/(game-page-layout)/play/[category]/[handle]/@newGamePage/getGameRewardsNotification/getFreebetReward';
import useGameContext from '~/client/components/Game/context/Game/useGameContext';
import createChangeCurrencyNotification from '~/client/components/Game/createChangeCurrencyNotification';
import useRewardsNotificationStore from '~/client/components/Game/hooks/useNotificationStore';
import useCurrencyChangeListener from '@/server/components/NavBar/LoggedInControls/UserControls/UserBalance/Balance/useBalance/useCurrencyChangeListener';
import getIsUbtcEnabled from '@/server/graphql/getCurrencyConfig/helpers/getIsUbtcEnabled';
import getCurrencyDisplayIcon from '@/shared/utils/getDisplayAmount/helpers/getCurrencyDisplayIcon';
import replaceVariables from '@/shared/utils/translations/replaceVariables';

const useChangeCurrencyNotification = (
  freebetNotification: RewardsNotification,
  inGameCurrency?: string,
  walletCurrency?: CashierCurrency,
) => {
  const { setNotifications, hasNotification } = useRewardsNotificationStore();
  const hasFreebetSwitchNotification = hasNotification('freeBetReward');
  const { game } = useGameContext();
  const notificationSwitchedCurrency = useRef(false);

  useCurrencyChangeListener({
    onChange: async () => {
      notificationSwitchedCurrency.current = true;
    },
  });

  const getRewardData = async () => {
    const { reward } = await getFreebetReward({
      inGameCurrency: inGameCurrency as Unit,
      walletCurrency: walletCurrency as Unit,
      gameId: game?.id,
      isGameCurrencyChangeEnabled:
        freebetNotification.isGameCurrencyChangeEnabled,
    });
    const isUbtcEnabled = await getIsUbtcEnabled(
      walletCurrency as CashierCurrency,
    );
    const rewardCurrencyIcon = getCurrencyDisplayIcon(
      reward?.currency as CashierCurrency,
      isUbtcEnabled,
    ) as Unit;
    addNotification(rewardCurrencyIcon);
  };

  const isInitialMount = useRef(true);

  const addNotification = (rewardCurrency?: string, rewardId?: string) => {
    if (
      !hasFreebetSwitchNotification &&
      freebetNotification.title &&
      rewardCurrency &&
      ((inGameCurrency && rewardCurrency !== inGameCurrency) ||
        rewardCurrency !== walletCurrency)
    ) {
      const IGC = inGameCurrency ?? rewardCurrency;
      const invalidCurrencyPairs =
        walletCurrency !== IGC && IGC === rewardCurrency;

      const getTranslationKey = () => {
        if (!freebetNotification.isGameCurrencyChangeEnabled) {
          return 'casino.in_order_to_claim_freebets';
        }
        if (invalidCurrencyPairs) {
          return 'casino.currency_switch_required_to_claim';
        }
        return 'casino.in_order_to_claim_freebets_in_game_currency';
      };

      const body = replaceVariables(
        freebetNotification.translations[getTranslationKey()],
        {
          currency: rewardCurrency,
          walletCurrency,
          inGameCurrency: inGameCurrency ?? undefined,
        },
      );
      createChangeCurrencyNotification(
        body,
        freebetNotification.title,
        freebetNotification.isRequiredToSwitch,
        rewardCurrency,
        rewardId ?? '',
      );
      setNotifications('freeBetReward');
    }
  };

  useEffect(() => {
    if (!isInitialMount.current) {
      if (notificationSwitchedCurrency.current === true) {
        notificationSwitchedCurrency.current = false;
      }
      getRewardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inGameCurrency]);

  useEffect(() => {
    if (!isInitialMount.current) {
      if (notificationSwitchedCurrency.current === true) {
        notificationSwitchedCurrency.current = false;
        if (inGameCurrency !== walletCurrency) {
          return;
        }
      }
      getRewardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletCurrency]);

  useEffect(() => {
    isInitialMount.current = false;
  }, [inGameCurrency, walletCurrency]);
};

export default useChangeCurrencyNotification;
